'use client';
/**
 * ./src/hooks/useDeviceDetectClient.ts
 *
 * hook to detect if the device is mobile based on checking if screen width is below maxScreenSizeBreakpoint
 * can pass in number or enum matching the Tailwind css breakpoints
 * note that our comparators are flipped from tailwind css classes (lg: md:) due to specifically tracking mobile (except xl)
 * assumes default for mobile of < 1044 (BREAKPOINT_MD)
 *
 * @copyright DesignShop, 2024
 * @since 30/09/2024
 */
import { useEffect, useState } from 'react';

import {
  BREAKPOINT_LG,
  BREAKPOINT_MD,
  BREAKPOINT_SM,
  BREAKPOINT_XS,
} from '@/modules/common/constants';

export enum ScreenSizeBreakpoint {
  Xl = 'xl',
  Lg = 'lg',
  Md = 'md',
  Sm = 'sm',
  Xs = 'xs',
}

const useDeviceDetectClient = (
  maxScreenSizeBreakpoint: number | ScreenSizeBreakpoint | string = BREAKPOINT_MD
): {
  isMobile: boolean;
  size: ScreenSizeBreakpoint;
} => {
  const initialInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200; // Default to desktop size

  const [isMobile, setIsMobile] = useState(
    initialInnerWidth <
      (Number(maxScreenSizeBreakpoint) ? Number(maxScreenSizeBreakpoint) : BREAKPOINT_MD)
  );

  const [size, setSize] = useState<ScreenSizeBreakpoint>(ScreenSizeBreakpoint.Xl);

  useEffect(() => {
    const handleResize = () => {
      const innerWidth = window.innerWidth;

      const isStrBreakpoint =
        maxScreenSizeBreakpoint in ScreenSizeBreakpoint ||
        typeof maxScreenSizeBreakpoint === 'string';

      let newIsMobile = false;
      let newSize = ScreenSizeBreakpoint.Xl;

      if (innerWidth >= BREAKPOINT_LG) {
        newSize = ScreenSizeBreakpoint.Xl;
      } else if (innerWidth < BREAKPOINT_LG && innerWidth >= BREAKPOINT_MD) {
        newSize = ScreenSizeBreakpoint.Lg;
      } else if (innerWidth < BREAKPOINT_MD && innerWidth >= BREAKPOINT_SM) {
        newSize = ScreenSizeBreakpoint.Md;
      } else if (innerWidth < BREAKPOINT_SM && innerWidth >= BREAKPOINT_XS) {
        newSize = ScreenSizeBreakpoint.Sm;
      } else if (innerWidth < BREAKPOINT_XS) {
        newSize = ScreenSizeBreakpoint.Xs;
      }

      if (isStrBreakpoint) {
        if (maxScreenSizeBreakpoint === 'xl') {
          newIsMobile = true;
        } else if (maxScreenSizeBreakpoint === 'lg') {
          newIsMobile = innerWidth < BREAKPOINT_LG;
        } else if (maxScreenSizeBreakpoint === 'md') {
          newIsMobile = innerWidth < BREAKPOINT_MD;
        } else if (maxScreenSizeBreakpoint === 'sm') {
          newIsMobile = innerWidth < BREAKPOINT_SM;
        } else {
          newIsMobile = innerWidth < BREAKPOINT_XS;
        }
      } else {
        newIsMobile = innerWidth < maxScreenSizeBreakpoint;
      }

      setIsMobile(newIsMobile);
      setSize(newSize);
    };

    window.addEventListener('resize', handleResize);
    window.onresize = handleResize;
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [maxScreenSizeBreakpoint]);

  return { isMobile, size };
};

export default useDeviceDetectClient;
